import { useEffect, useRef, useState } from "react";

type CleanupFunction = () => void;
type AsyncFunction = (signal: AbortSignal) => Promise<void | CleanupFunction>;
type Options = {
  debounce?: number;
};

export const useEffectAsync = (
  func: AsyncFunction,
  deps: unknown[] = [],
  options: Options = {}
): void => {
  const debounce = options?.debounce;

  const [debouncedDeps, setDebouncedDeps] = useState<unknown[]>(deps);

  const cancelCallbackRef = useRef<null | CleanupFunction>(null);

  const silentIfAborted = async (
    callback: AsyncFunction,
    signal: AbortSignal
  ): Promise<void> => {
    try {
      const cleanup = await callback(signal);
      if (cleanup) {
        cancelCallbackRef.current = cleanup;
      }
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      throw e;
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedDeps(deps), debounce ?? 0);
    return () => clearTimeout(timeout);
  }, [...(deps ?? []), debounce]);

  useEffect(() => {
    const controller = new AbortController();
    void silentIfAborted(func, controller.signal);
    return () => {
      if (cancelCallbackRef.current) {
        try {
          cancelCallbackRef.current();
        } finally {
          cancelCallbackRef.current = null;
        }
      }
      controller.abort();
    };
  }, debouncedDeps);
};
